import { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Badge = ({ ok, label }) => (
  <span className={`text-xs px-2 py-0.5 rounded ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{ok ? "✓" : "✗"} {label}</span>
);

export const BestandenOhnePruefung = () => {
  const [data, setData] = useState(null);
  const location = useLocation();
  const urlFilter = new URLSearchParams(location.search).get("filter");
  const [offen, setOffen] = useState(!!urlFilter);
  const [jahr, setJahr] = useState("2026");
  const [filter, setFilter] = useState(urlFilter || "zert");

  useEffect(() => {
    if (urlFilter) { setFilter(urlFilter); setOffen(true); }
  }, [urlFilter]);

  useEffect(() => {
    axios.get(`${API}/report/bestanden-ohne-pruefung?jahr=${jahr}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [jahr]);

  if (!data) return null;

  const liste = data.liste.filter((r) => (filter === "zert" ? r.fehlt_zertifikat : filter === "pr" ? r.fehlt_pruefung_upload : true));
  const Tab = ({ id, label }) => (
    <button onClick={() => { setFilter(id); setOffen(true); }} data-testid={`bop-filter-${id}`}
      className={`px-3 py-1 rounded-full text-xs border ${filter === id ? "bg-amber-700 text-white border-amber-700" : "border-amber-300 text-amber-800"}`}>{label}</button>
  );

  const exportCsv = () => {
    const cols = ["name", "kurs", "finanzierung", "pruefung_am", "pruefung_2_am", "fehlt_zertifikat", "fehlt_pruefung_upload"];
    const esc = (v) => `"${String(v === true ? "FEHLT" : v === false ? "ok" : v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(";"), ...liste.map((r) => cols.map((c) => esc(r[c])).join(";"))];
    const url = URL.createObjectURL(new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `Bestanden_ohne_Zertifikat_oder_Pruefung_${jahr}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Card className="card-custom mb-6 border-amber-300" data-testid="bestanden-ohne-pruefung-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-amber-800">
          <AlertTriangle size={18} /> Bestanden angeklickt – aber Zertifikat oder Prüfungs-Upload fehlt
          <select value={jahr} onChange={(e) => setJahr(e.target.value)} className="ml-2 text-sm border rounded px-1" data-testid="bestanden-ohne-pruefung-jahr">
            <option value="2026">2026</option><option value="2025">2025</option><option value="alle">alle Jahre</option>
          </select>
        </CardTitle>
        <p className="text-sm text-stone-700" data-testid="bestanden-ohne-pruefung-summary">
          <b>{data.offen}</b> von {data.bestanden_gesamt} bestandenen · <b>{data.ohne_zertifikat}</b> ohne Zertifikat · <b>{data.ohne_pruefung_upload}</b> ohne hochgeladene Prüfung
          {Object.keys(data.nach_finanzierung).length > 0 && <span className="text-stone-500"> · {Object.entries(data.nach_finanzierung).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>}
        </p>
        {data.offen > 0 && (
          <p className="text-xs flex items-center gap-2 flex-wrap mt-1">
            <Tab id="zert" label={`Ohne Zertifikat (${data.ohne_zertifikat})`} />
            <Tab id="pr" label={`Ohne Prüfungs-Upload (${data.ohne_pruefung_upload})`} />
            <Tab id="alle" label={`Alle (${data.offen})`} />
            <button className="underline text-amber-800 ml-2" onClick={() => setOffen(!offen)} data-testid="bestanden-ohne-pruefung-toggle">{offen ? "Liste ausblenden" : "Liste anzeigen"}</button>
            <button className="ml-3 underline text-amber-800" onClick={exportCsv} data-testid="bestanden-ohne-pruefung-csv">CSV exportieren</button>
          </p>
        )}
      </CardHeader>
      {offen && (
        <CardContent className="pt-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-stone-500 border-b">
                <th className="py-1">Teilnehmer</th><th>Kurs</th><th>Finanzierung</th><th>Prüfung am</th><th>Zertifikat</th><th>Prüfung hochgeladen</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((r, i) => (
                <tr key={`${r.teilnehmer_id}-${i}`} className="border-b last:border-0" data-testid={`bestanden-ohne-pruefung-row-${i}`}>
                  <td className="py-1.5 font-medium">{r.name}</td>
                  <td>{r.kurs || "—"}</td>
                  <td>{r.finanzierung}</td>
                  <td>{r.pruefung_2_am || r.pruefung_am || "—"}</td>
                  <td><Badge ok={!r.fehlt_zertifikat} label="Zertifikat" /></td>
                  <td><Badge ok={!r.fehlt_pruefung_upload} label="Prüfung" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
};
